import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Divider,
  IconButton,
  Box,
  Chip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { ReactComponent as ResetIcon } from '../../assets/ICONS/resetIcon.svg';
import './costAnalysis.css';
import { useFetchData } from '../../hooks/useFetchData';
import { config } from './config';
import Loading from '../../components/Loading/Loading';

export interface FilterOption {
  id: string;
  label: string;
}

export interface FilterCategory {
  id: string;
  label: string;
}

const createCategoryKey = (category: FilterCategory): string => {
  return `${category.id}:${category.label}`;
};

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  categories: FilterCategory[];
  selectedCategory: string | null;
  selectedOptions: Record<string, string[]>;
  onSelectCategory: (categoryId: string, categoryLabel: string) => void;
  onSelectOptions: (categoryId: string, options: string[]) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  // UI-specific selections for each unique category (by categoryKey)
  uiSelectedOptionsByCategory: Record<string, string[]>;
  onUpdateUiSelections: (categoryKey: string, options: string[]) => void;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  categories,
  selectedCategory,
  selectedOptions,
  onSelectCategory,
  onSelectOptions,
  onApplyFilters,
  onResetFilters,
  uiSelectedOptionsByCategory,
  onUpdateUiSelections,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [lastSelectedCategory, setLastSelectedCategory] = useState<
    string | null
  >(null);
  const [
    selectedCategoryData,
    setSelectedCategoryData,
  ] = useState<FilterCategory | null>(null);

  const {
    data: categoryOptions,
    loader: isFetchingOptions,
    fetch: fetchCategoryOptions,
  } = useFetchData({
    fetchData: (filterName: string) =>
      config.getCategoryOptions({ filterName }),
    fetchOnMount: false,
  });

  // store the last selected category to prevent recalculations
  useEffect(() => {
    if (selectedCategory !== lastSelectedCategory) {
      setLastSelectedCategory(selectedCategory);
      setSearchQuery('');

      if (selectedCategory) {
        const category = categories.find(
          (cat) => createCategoryKey(cat) === selectedCategory
        );
        if (category) {
          setSelectedCategoryData(category);
          fetchCategoryOptions(category.id);
        }
      } else {
        setSelectedCategoryData(null);
      }
    }
  }, [selectedCategory, categories, fetchCategoryOptions]);

  const handleSelectCategory = (category: FilterCategory) => {
    onSelectCategory(category.id, category.label);
  };

  const getCurrentUiSelections = (): string[] => {
    if (!selectedCategory) return [];
    return uiSelectedOptionsByCategory[selectedCategory] || [];
  };

  const handleToggleOption = (optionLabel: string) => {
    if (!selectedCategoryData || !selectedCategory) return;

    const categoryId = selectedCategoryData.id;
    const currentUiSelected = getCurrentUiSelections();

    const newUiSelected = currentUiSelected.includes(optionLabel)
      ? currentUiSelected.filter((label) => label !== optionLabel)
      : [...currentUiSelected, optionLabel];

    // Update UI selections for this specific category
    onUpdateUiSelections(selectedCategory, newUiSelected);

    // Calculate merged distinct options for API call immediately
    updateApiSelectionsImmediate(categoryId, selectedCategory, newUiSelected);
  };

  const handleSelectAll = (select: boolean) => {
    if (!selectedCategoryData || !selectedCategory) return;

    const categoryId = selectedCategoryData.id;
    const filteredOptions = getFilteredOptions().map((opt) => opt);

    let newUiSelected: string[] = [];
    if (select && filteredOptions.length > 0) {
      newUiSelected = filteredOptions;
    }

    // Update UI selections for this specific category
    onUpdateUiSelections(selectedCategory, newUiSelected);

    // Calculate merged distinct options for API call immediately
    updateApiSelectionsImmediate(categoryId, selectedCategory, newUiSelected);
  };

  const getFilteredOptions = () => {
    if (!categoryOptions || !Array.isArray(categoryOptions)) return [];

    return categoryOptions.filter(
      (opt) =>
        opt &&
        (searchQuery === '' ||
          opt.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  const filteredOptions = useMemo(() => {
    return getFilteredOptions();
  }, [categoryOptions, searchQuery]);

  const isAllSelected = () => {
    if (!selectedCategoryData) return false;
    if (filteredOptions.length === 0) return false;

    const currentUiSelected = getCurrentUiSelections();
    return (
      filteredOptions.length > 0 &&
      filteredOptions.every((opt) => currentUiSelected.includes(opt))
    );
  };

  const isApplyEnabled = () => {
    return Object.values(selectedOptions).some((options) => options.length > 0);
  };

  // Handle removing a selected option from the main view
  const handleRemoveSelectedOption = (
    category: FilterCategory,
    optionToRemove: string
  ) => {
    const categoryKey = createCategoryKey(category);
    const currentUiSelected = uiSelectedOptionsByCategory[categoryKey] || [];
    const newUiSelected = currentUiSelected.filter(
      (opt) => opt !== optionToRemove
    );

    // Update UI selections for this specific category key
    onUpdateUiSelections(categoryKey, newUiSelected);

    // Calculate merged distinct options for API call immediately
    updateApiSelectionsImmediate(category.id, categoryKey, newUiSelected);
  };

  // Helper function to update API selections with merged distinct options - immediate version
  const updateApiSelectionsImmediate = (
    categoryId: string,
    updatedCategoryKey: string,
    updatedSelections: string[]
  ) => {
    const categoriesWithSameId = categories.filter(
      (cat) => cat.id === categoryId
    );
    const allOptionsForId = new Set<string>();

    // Collect all selections from categories with the same ID
    categoriesWithSameId.forEach((cat) => {
      const catKey = createCategoryKey(cat);
      let selections: string[] = [];

      if (catKey === updatedCategoryKey) {
        // Use the new selections for the updated category
        selections = updatedSelections;
      } else {
        // Use existing selections for other categories
        selections = uiSelectedOptionsByCategory[catKey] || [];
      }

      selections.forEach((option) => allOptionsForId.add(option));
    });

    const mergedDistinctOptions = Array.from(allOptionsForId);

    // Update or remove the selectedFilterOptions based on merged results
    if (mergedDistinctOptions.length === 0) {
      onSelectOptions(categoryId, []);
    } else {
      onSelectOptions(categoryId, mergedDistinctOptions);
    }
  };

  const renderSelectedOptions = (category: FilterCategory) => {
    const categoryKey = createCategoryKey(category);
    const selectedForCategory = uiSelectedOptionsByCategory[categoryKey] || [];

    if (selectedForCategory.length === 0) return null;

    return (
      <div className="selectedOptionsContainer">
        <div className="selectedOptionsList">
          {selectedForCategory.map((option, index) => (
            <Chip
              key={`${category.id}-${option}-${index}`}
              label={option}
              onDelete={() => handleRemoveSelectedOption(category, option)}
              className="selectedOptionChip"
              size="small"
              deleteIcon={<CloseIcon />}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderMainView = () => (
    <div className="filterCategoryView">
      <div className="filterCategoryHeader">
        <p>Filter</p>
        <Box flexGrow={1} />
        <Button
          onClick={onResetFilters}
          className="resetButton"
          startIcon={<ResetIcon />}
        >
          Reset All
        </Button>
      </div>
      <Divider />
      <div className="optionsList" style={{ maxHeight: '100%' }}>
        {categories.map((category) => {
          const categoryKey = createCategoryKey(category);
          const hasUiSelections =
            (uiSelectedOptionsByCategory[categoryKey] || []).length > 0;

          return (
            <div key={categoryKey} className="categoryItemWrapper">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasUiSelections}
                    onClick={() => handleSelectCategory(category)}
                    className="categoryCheckbox"
                  />
                }
                label={category.label}
                className="optionItem"
              />
              {renderSelectedOptions(category)}
            </div>
          );
        })}
      </div>
      <Box className="filterActions">
        <Button onClick={onClose} variant="outlined" className="cancelButton">
          Cancel
        </Button>
        <Button
          onClick={onApplyFilters}
          variant="contained"
          color="primary"
          className="applyButton"
          disabled={!isApplyEnabled()}
        >
          Apply
        </Button>
      </Box>
    </div>
  );

  const renderCategoryView = () => {
    if (!selectedCategoryData) return null;

    const currentUiSelected = getCurrentUiSelections();

    return (
      <div className="filterCategoryView">
        <div className="filterCategoryHeader">
          <IconButton
            className="backButton"
            onClick={() => onSelectCategory('', '')}
          >
            <ArrowBackIcon />
          </IconButton>
          <p>{selectedCategoryData.label}</p>
          <Box flexGrow={1} />
          <Button
            onClick={onResetFilters}
            className="resetButton"
            startIcon={<ResetIcon />}
          >
            Reset All
          </Button>
        </div>
        <Divider />
        <div className="searchAnalysisContainer">
          <SearchIcon className="searchIcon" />
          <TextField
            placeholder="Search"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="searchInput"
          />
        </div>
        <div className="selectAllContainer">
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllSelected()}
                indeterminate={
                  currentUiSelected.length > 0 &&
                  currentUiSelected.length < filteredOptions.length
                }
                onChange={(e) => handleSelectAll(e.target.checked)}
                color="primary"
                className="categoryCheckbox"
              />
            }
            label="Select All"
          />
        </div>
        <div className="optionsList">
          {isFetchingOptions ? (
            <Box
              display="flex"
              justifyContent="center"
              marginTop={'50%'}
              padding={4}
            >
              <Loading />
            </Box>
          ) : (
            <>
              {filteredOptions.map((option, index) => (
                <FormControlLabel
                  key={`${selectedCategoryData.id}-${option}-${index}`}
                  control={
                    <Checkbox
                      checked={currentUiSelected.includes(option)}
                      onChange={() => handleToggleOption(option)}
                      className="categoryCheckbox"
                    />
                  }
                  label={option}
                  className="optionItem"
                />
              ))}
              {filteredOptions.length === 0 && !isFetchingOptions && (
                <Typography className="noResults">No results found</Typography>
              )}
            </>
          )}
        </div>

        <Box className="filterActions">
          <Button onClick={onClose} variant="outlined" className="cancelButton">
            Cancel
          </Button>
          <Button
            onClick={onApplyFilters}
            variant="contained"
            color="primary"
            className="applyButton"
            disabled={!isApplyEnabled()}
          >
            Apply
          </Button>
        </Box>
      </div>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="filterDrawer"
    >
      <div className="filterDrawerContent">
        <div className="drawerHeader">
          <IconButton onClick={onClose} className="closeButton">
            <CloseIcon />
          </IconButton>
        </div>

        {selectedCategory ? renderCategoryView() : renderMainView()}
      </div>
    </Drawer>
  );
};

export default FilterDrawer;
